import { useState, useEffect, useRef, useCallback } from 'react';
import { SpotlightActionData } from '@mantine/spotlight';
import { useDebouncedValue } from '@mantine/hooks';
import { rem } from '@mantine/core';
import {
  IconCalendarPlus,
  IconUserPlus,
  IconSearch,
  IconMapPin,
  IconCalendarWeek,
  IconHelp,
} from '@tabler/icons-react';
import {
  SearchResult,
  SearchResponse,
  searchAPI,
  convertToSpotlightActions,
  createRecentlyViewedActions,
  createDanceStyleSuggestionActions,
  fetchSearchSuggestions,
  type PopularDanceStyle,
  reportSearchEvent,
  createSeeAllResultsAction,
  shouldShowSeeAllAction,
  SEARCH_DEBOUNCE_TIME,
  getGroupName,
  getIconForType,
  getEventDescriptionColor,
} from '../utils/searchHelpers';
import { clearRecentlyViewed, getRecentlyViewed, type RecentlyViewedItem } from '../../../src/utils/recentlyViewed';

// Quick actions shown when search is empty
const createQuickActions = (): SpotlightActionData[] => {
  const iconStyle = { width: rem(24), height: rem(24) };
  const iconColor = '#EA580C';

  return [
    {
      id: 'quick-add-event',
      label: 'Add a New Event',
      description: 'Submit a dance event to the calendar',
      onClick: () => (window.location.href = '/suggest_event'),
      leftSection: <IconCalendarPlus style={iconStyle} color={iconColor} />,
      group: 'Quick Actions',
    },
    {
      id: 'quick-add-artist',
      label: 'Add a New Artist',
      description: 'Submit an artist profile',
      onClick: () => (window.location.href = '/suggest_artist'),
      leftSection: <IconUserPlus style={iconStyle} color={iconColor} />,
      group: 'Quick Actions',
    },
    {
      id: 'quick-explore-events',
      label: 'Explore Events',
      description: 'Browse all upcoming dance events',
      onClick: () => (window.location.href = '/events'),
      leftSection: <IconSearch style={iconStyle} color={iconColor} />,
      group: 'Quick Actions',
    },
    {
      id: 'quick-explore-cities',
      label: 'Explore Cities',
      description: 'Find events in your city',
      onClick: () => (window.location.href = '/cities'),
      leftSection: <IconMapPin style={iconStyle} color={iconColor} />,
      group: 'Quick Actions',
    },
    {
      id: 'quick-dance-styles',
      label: 'Dance Style Calendars',
      description: 'Browse events by dance style',
      onClick: () => (window.location.href = '/dance_styles'),
      leftSection: <IconCalendarWeek style={iconStyle} color={iconColor} />,
      group: 'Quick Actions',
    },
    {
      id: 'quick-help-center',
      label: 'Help Center',
      description: 'Get help and learn how to use WCWD',
      onClick: () => (window.location.href = '/help'),
      leftSection: <IconHelp style={iconStyle} color={iconColor} />,
      group: 'Quick Actions',
    },
  ];
};

export const useSearch = () => {
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchActions, setSearchActions] = useState<SpotlightActionData[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showFullResults, setShowFullResults] = useState(false);
  const [fullSearchResults, setFullSearchResults] = useState<SearchResponse | null>(null);
  const [fullSearchLoading, setFullSearchLoading] = useState(false);
  const [lastResponse, setLastResponse] = useState<SearchResponse | null>(null);
  // Read from localStorage when the panel opens, so it's current (#1504)
  const [recentlyViewed, setRecentlyViewed] = useState<RecentlyViewedItem[]>([]);
  const [currentPath, setCurrentPath] = useState('');
  // Popular styles for the empty panel, fetched once on first open
  const [suggestedStyles, setSuggestedStyles] = useState<PopularDanceStyle[]>([]);
  const suggestionsRequestedRef = useRef(false);
  // The query already reported, so a click followed by close counts once
  const reportedQueryRef = useRef<string | null>(null);

  const [debouncedQuery] = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_TIME);

  // Report the search the user settled on (#1502). result_count is null when
  // they left before results for this exact query arrived.
  const reportSearch = useCallback(
    (clicked?: SearchResult) => {
      const query = searchQuery.trim();
      if (!query || reportedQueryRef.current === query) return;

      reportedQueryRef.current = query;
      reportSearchEvent({
        query,
        result_count: lastResponse?.query === query ? lastResponse.results.length : null,
        clicked_result_type: clicked?.type,
        clicked_result_id: clicked?.id,
      });
    },
    [searchQuery, lastResponse],
  );

  // Kept in a ref so result actions built in the fetch effect always report
  // with the current query and response.
  const reportSearchRef = useRef(reportSearch);
  reportSearchRef.current = reportSearch;

  // Perform search when debounced query changes
  useEffect(() => {
    if (debouncedQuery.trim()) {
      setIsSearching(true);
      searchAPI(debouncedQuery)
        .then((response) => {
          const actions = convertToSpotlightActions(response.results, (result) => reportSearchRef.current(result));
          setSearchResults(response.results);
          setSearchActions(actions);
          setLastResponse(response);
        })
        .finally(() => setIsSearching(false));
    } else {
      setSearchResults([]);
      setSearchActions([]);
      setLastResponse(null);
    }
  }, [debouncedQuery]);

  // Covers the debounce wait too, so the spinner shows as soon as typing starts
  const isPending = isSearching || searchQuery.trim() !== debouncedQuery.trim();

  // Handlers
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    if (query.trim() !== reportedQueryRef.current) reportedQueryRef.current = null;
  };

  const handleSpotlightClose = () => reportSearch();

  const handleSpotlightOpen = () => {
    setRecentlyViewed(getRecentlyViewed());
    setCurrentPath(window.location.pathname);
    if (!suggestionsRequestedRef.current) {
      suggestionsRequestedRef.current = true;
      fetchSearchSuggestions().then(setSuggestedStyles);
    }
  };

  const handleClearRecentlyViewed = () => {
    clearRecentlyViewed();
    setRecentlyViewed([]);
  };

  const handleShowAllResults = async () => {
    if (!searchQuery.trim()) return;

    reportSearch();
    setShowFullResults(true);

    // The spotlight already fetched this exact query; reuse it
    if (lastResponse && lastResponse.query === searchQuery.trim()) {
      setFullSearchResults(lastResponse);
      return;
    }

    setFullSearchLoading(true);
    try {
      const response = await searchAPI(searchQuery);
      setFullSearchResults(response);
    } finally {
      setFullSearchLoading(false);
    }
  };

  const handleBackToSearch = () => {
    setShowFullResults(false);
  };

  const closeFullResults = () => {
    setShowFullResults(false);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchActions([]);
    setFullSearchResults(null);
    setLastResponse(null);
  };

  // Generate spotlight actions:
  // - When query is empty: recently viewed pages, popular styles, then quick actions
  // - When query has results: show search results + "see all" action
  const spotlightActions: SpotlightActionData[] = (() => {
    const trimmedQuery = searchQuery.trim();

    // If no query, show quick actions
    if (!trimmedQuery) {
      return [
        ...createRecentlyViewedActions(recentlyViewed, currentPath, handleClearRecentlyViewed),
        ...createDanceStyleSuggestionActions(suggestedStyles.slice(0, 6)),
        ...createQuickActions(),
      ];
    }

    // If we have a query, show search results
    return [
      ...searchActions,
      ...(shouldShowSeeAllAction(searchQuery, searchActions.length > 0)
        ? [createSeeAllResultsAction(searchQuery, handleShowAllResults)]
        : []),
    ];
  })();

  return {
    // State
    searchQuery,
    searchActions,
    searchResults,
    isSearching: isPending,
    showFullResults,
    fullSearchResults,
    fullSearchLoading,
    spotlightActions,

    // Handlers
    handleSearchChange,
    handleSpotlightClose,
    handleSpotlightOpen,
    handleShowAllResults,
    handleBackToSearch,
    closeFullResults,
    clearSearch,

    // Re-exported utilities for components that use this hook
    getGroupName,
    getIconForType,
    getEventDescriptionColor,
  };
};

// Re-export types for convenience
export type { SearchResult, SearchResponse };
